/* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { debounce } from 'throttle-debounce';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type { FundProps } from '@sola/schema/lib/schema.ts';
import { trackVolunteerSearch, trackVolunteerViewContent, trackVolunteerViewContentList } from '../lib/analytics/events-volunteer.ts';
import type { ChildSearchEvent } from '../lib/analytics/events.ts';
import { trackChildSearch, trackError, trackFunnelPage, trackOptimizelyPageView, trackOptimizelyResults, trackPageView, trackViewBio, trackViewContent, trackViewFund, trackViewFundItemList, trackViewSacItemList } from '../lib/analytics/events.ts';
import { type VolunteerLocationInfo, type VolunteerSearchFilters, buildVolunteerSearchParams, resolveVolunteerListIdentity, resolveVolunteerListView, resolveVolunteerSearchTrack } from '../lib/analytics/mappings-volunteer.ts';
import { encodeExperiments } from '../lib/experiments.ts';
import type { OptimizelyImpressionResponse } from '../lib/optimizely-schemas.ts';
export const TrackPageView: FC<{
  experiments?: string[];
}> = t0 => {
  const $ = _c(4);
  const {
    experiments
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== experiments) {
    t1 = () => {
      trackPageView(experiments);
    };
    $[0] = experiments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pathname) {
    t2 = [pathname];
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};
export const TrackVolunteerViewContent: FC<{
  location: VolunteerLocationInfo;
  tourLocations?: VolunteerLocationInfo[];
  title?: string | null;
}> = t0 => {
  const $ = _c(6);
  const {
    location,
    tourLocations,
    title
  } = t0;
  const hasTrackedViewRef = useRef(null);
  let t1;
  if ($[0] !== location || $[1] !== title || $[2] !== tourLocations) {
    t1 = () => {
      if (hasTrackedViewRef.current === location.eventId) {
        return;
      }
      hasTrackedViewRef.current = location.eventId;
      trackVolunteerViewContent({
        location,
        tourLocations,
        title
      });
    };
    $[0] = location;
    $[1] = title;
    $[2] = tourLocations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== location.eventId) {
    t2 = [location.eventId];
    $[4] = location.eventId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
};

/**
 * Fires both the GA funnel_page_view dataLayer event and the Optimizely
 * page_views conversion event on navigation. Combines the previous
 * TrackFunnelPage and TrackOptimizelyPageView into a single component.
 */
export const TrackFunnelPage: FC<{
  funnel?: string | null;
}> = t0 => {
  const $ = _c(4);
  const {
    funnel
  } = t0;
  const pathname = usePathname();
  let t1;
  let t2;
  if ($[0] !== funnel || $[1] !== pathname) {
    t1 = () => {
      trackFunnelPage(funnel);
      trackOptimizelyPageView(pathname, funnel);
    };
    t2 = [pathname, funnel];
    $[0] = funnel;
    $[1] = pathname;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};
export const TrackViewContent: FC<{
  cmsId: string;
  title?: string;
  type?: string;
  tags?: string[];
}> = t0 => {
  const $ = _c(6);
  const {
    cmsId,
    title,
    type,
    tags
  } = t0;
  let t1;
  let t2;
  if ($[0] !== cmsId || $[1] !== tags || $[2] !== title || $[3] !== type) {
    t1 = () => {
      trackViewContent(cmsId, title || "Unknown", type || "Unknown", tags || []);
    };
    t2 = [cmsId, title, type, tags];
    $[0] = cmsId;
    $[1] = tags;
    $[2] = title;
    $[3] = type;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
};
export const TrackChildSearch: FC<{
  event: ChildSearchEvent;
}> = t0 => {
  const $ = _c(5);
  const {
    event
  } = t0;
  let t1;
  let t2;
  if ($[0] !== event) {
    t1 = () => {
      debounce(5000, () => {
        trackChildSearch(event);
      })();
    };
    t2 = JSON.stringify(event);
    $[0] = event;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = [t2];
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t1, t3);
  return null;
};
export const TrackViewFund: FC<{
  fund?: FundProps;
}> = t0 => {
  const $ = _c(4);
  const {
    fund
  } = t0;
  let t1;
  if ($[0] !== fund) {
    t1 = () => {
      if (!fund) {
        return;
      }
      trackViewFund(fund);
    };
    $[0] = fund;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = fund?.id;
  let t3;
  if ($[2] !== t2) {
    t3 = [t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t1, t3);
  return null;
};
export const TrackViewFundItemList: FC<{
  listId: string;
  listName: string;
  items: FundProps[];
  isCatalog?: boolean;
}> = t0 => {
  const $ = _c(11);
  const {
    listId,
    listName,
    items,
    isCatalog: t1
  } = t0;
  const isCatalog = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== isCatalog || $[1] !== items || $[2] !== listId || $[3] !== listName) {
    t2 = () => {
      if (!items?.length) {
        return;
      }
      trackViewFundItemList(listId, listName, items, isCatalog);
    };
    $[0] = isCatalog;
    $[1] = items;
    $[2] = listId;
    $[3] = listName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== items) {
    t3 = JSON.stringify(items);
    $[5] = items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== listId || $[8] !== listName || $[9] !== t3) {
    t4 = [listId, listName, t3];
    $[7] = listId;
    $[8] = listName;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t2, t4);
  return null;
};
export const TrackViewSacItemList: FC<{
  listId: string;
  listName: string;
  items: ChildAvailabilityItem[];
}> = t0 => {
  const $ = _c(10);
  const {
    listId,
    listName,
    items
  } = t0;
  let t1;
  if ($[0] !== items || $[1] !== listId || $[2] !== listName) {
    t1 = () => {
      if (!items?.length) {
        return;
      }
      trackViewSacItemList(listId, listName, items);
    };
    $[0] = items;
    $[1] = listId;
    $[2] = listName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== items) {
    t2 = JSON.stringify(items);
    $[4] = items;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== listId || $[7] !== listName || $[8] !== t2) {
    t3 = [listId, listName, t2];
    $[6] = listId;
    $[7] = listName;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t1, t3);
  return null;
};
export const TrackViewBio: FC<{
  child: ChildAvailabilityItem;
}> = t0 => {
  const $ = _c(4);
  const {
    child
  } = t0;
  let t1;
  if ($[0] !== child) {
    t1 = () => {
      if (!child) {
        return;
      }
      trackViewBio(child);
    };
    $[0] = child;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== child.globalId) {
    t2 = [child.globalId];
    $[2] = child.globalId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};
export const TrackError: FC<{
  errorMessage?: string;
}> = t0 => {
  const $ = _c(3);
  const {
    errorMessage
  } = t0;
  let t1;
  let t2;
  if ($[0] !== errorMessage) {
    t1 = () => {
      trackError(errorMessage);
    };
    t2 = [errorMessage];
    $[0] = errorMessage;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
};
type TrackOptimizelyResultsProps = {
  renderedExperiments: string[];
};
export const TrackOptimizelyResults: FC<TrackOptimizelyResultsProps> = ({
  renderedExperiments
}) => {
  const pathname = usePathname();
  const renderedEncoded = encodeExperiments(renderedExperiments);
  useEffect(() => {
    const trackResults = async () => {
      try {
        const params = new URLSearchParams(globalThis?.location?.search);
        params.set('pathname', pathname);
        params.set('rendered_experiments', renderedEncoded);
        const response = await fetch(`/api/experiments?${params.toString()}`);
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as OptimizelyImpressionResponse;
        if (!data?.results?.length) {
          return;
        }
        trackOptimizelyResults(data);
      } catch (e) {
        // Do nothing if the API call fails.
        console.error(e);
      }
    };
    trackResults();
  }, [pathname, renderedEncoded]);
  return null;
};
export interface TrackVolunteerListProps {
  page: number;
  /** Pass the stable `data.items` reference, not a fresh slice, so effects only re-run on real data changes. */
  items: VolunteerLocationInfo[] | undefined;
  limit: number;
  sortBy: string;
  query: string;
  isFetching: boolean;
  /** Gate for view_content_list: hold the impression until the caller's list
   * has settled. Defaults to true for callers with no async location. */
  ready?: boolean;
  slug?: string;
  /** Tour display name, used to build the tour listing's content_list_name. */
  tourName?: string | null;
}

/**
 * Null-rendering `Track*` component that fires volunteer list impressions:
 * `view_content_list` (once per distinct list) and `volunteer_search` (on
 * sort/query change). Pure decision logic lives in `lib/analytics/mappings-volunteer.ts`.
 */
export const TrackVolunteerList: FC<TrackVolunteerListProps> = ({
  page,
  items,
  limit,
  sortBy,
  query,
  isFetching,
  ready = true,
  slug,
  tourName
}) => {
  const lastTrackedSignatureRef = useRef<string | null>(null);

  // Reset dedupe on navigation: the client instance can survive App Router
  // navigations, and re-entering the list is a genuine new view.
  const pathname = usePathname();
  const lastPathnameRef = useRef(pathname);
  if (lastPathnameRef.current !== pathname) {
    lastPathnameRef.current = pathname;
    lastTrackedSignatureRef.current = null;
  }

  // The sort/query last reported to volunteer_search. Seeded with the mount
  // values so nuqs syncing URL defaults on mount isn't mistaken for a user
  // change; updated whenever a search fires so reverting a filter to its
  // previous value doesn't refire.
  const lastTrackedFiltersRef = useRef<VolunteerSearchFilters>({
    sortBy,
    query
  });
  const locations = (items ?? []).slice(0, limit);

  // Fire view_content_list once results arrive, held until `ready` so
  // transient results during location resolution don't each fire an impression.
  useEffect(() => {
    if (!ready) {
      return;
    }
    const view = resolveVolunteerListView(page, locations, lastTrackedSignatureRef.current);
    if (view) {
      lastTrackedSignatureRef.current = view.signature;
      const identity = resolveVolunteerListIdentity({
        slug,
        tourName
      });
      trackVolunteerViewContentList({
        locations,
        contentListId: identity.contentListId,
        contentListName: identity.contentListName
      });
    }

    // Keyed on the stable `items` ref, not the derived slice.
  }, [items, limit, page, slug, tourName, ready]);

  // Fire volunteer_search once results for a new filter set have settled.
  //
  // A single settled-state effect replaces the earlier queue/flush/fallback
  // split: while `isFetching` is false, `items` already holds the results for
  // the current filters, so this covers both the refetch case (fires when new
  // items land) and the no-refetch case (e.g. sort by location without geo,
  // where the query key is unchanged and fetching never toggles).
  useEffect(() => {
    if (isFetching) {
      return;
    }
    const changed = resolveVolunteerSearchTrack({
      sortBy,
      query
    }, lastTrackedFiltersRef.current);
    if (!changed) {
      return;
    }
    lastTrackedFiltersRef.current = changed;
    trackVolunteerSearch(buildVolunteerSearchParams({
      sortBy,
      query,
      slug,
      tourName,
      locations
    }));
    // `items` is in deps so the effect re-evaluates when results land; the
    // derived `locations` slice is read at fire time.
  }, [isFetching, sortBy, query, items, limit, slug, tourName]);
  return null;
};